// VENDORED COPY of frontend/src/lib/financial/product-net-value.ts — PARTIAL.
// Only the pure, synchronous `computeProductKPIs` is vendored into the portal.
// The async `calculateProductNetValue` facade and its `window.ProductCost` /
// `window.ProductSavings` default-deps are intentionally OMITTED: the portal
// has no WebOS/AppLib globals and fetches data via its own typed API client,
// so the overlay calls `computeProductKPIs` directly with pre-fetched buckets.
// (Plan US3 requires `computeProductKPIs`; the no-`window.*` constraint forbids
// vendoring the window-dependent facade.)

import { calculateProductPL } from './product-pl';
import { calculateKPIs } from './kpis';
import type { HorizonResult, KPIResult } from './types';

// ── Pure synchronous calculation ─────────────────────────────────────────────

type RawBucket = { one_time: ReadonlyArray<Record<string, unknown>>; recurring: ReadonlyArray<Record<string, unknown>> };

/**
 * Compute 36-month KPIs from pre-fetched costs and savings using the P&L model.
 *
 * This is the single canonical calculation path:
 *   CAPEX  → amortized as D&A (cost / useful_life_months per month)
 *   OPEX-COGS → subtracted above gross margin
 *   OPEX-SGA  → subtracted below gross margin
 *   Revenue   → Σ savings
 *
 * Call this wherever KPIs are needed. Never pass raw cost items through
 * calculateHorizon directly — that skips amortization and produces Path B results.
 */
export function computeProductKPIs(
  costsData: RawBucket,
  savingsData: RawBucket,
  horizonMonths = 36,
): KPIResult {
  const pl = calculateProductPL(
    { one_time: costsData.one_time as Array<Record<string, unknown>>, recurring: costsData.recurring as Array<Record<string, unknown>> },
    { one_time: savingsData.one_time as Array<Record<string, unknown>>, recurring: savingsData.recurring as Array<Record<string, unknown>> },
    { horizonMonths },
  );
  const totalCostMonthly = pl.cogs.monthly.map((v, i) => v + pl.sga.monthly[i] + pl.da.monthly[i]);
  const costHorizon: HorizonResult = {
    monthly: totalCostMonthly,
    total: pl.cogs.total + pl.sga.total + pl.da.total,
    items: [],
  };
  const valueHorizon: HorizonResult = {
    monthly: pl.revenue.monthly,
    total: pl.revenue.total,
    items: [],
  };
  return calculateKPIs(valueHorizon, costHorizon);
}
